/**
 * Expenses query hook
 * Fetches property expenses with pagination and filters
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Expense, ExpenseFilters } from "@/types/admin/expense";

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface PaginatedResult<T> {
  data: T[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

/**
 * Hook to fetch expenses with pagination and filters
 */
export const useExpenses = (filters?: ExpenseFilters, pagination?: PaginationParams) => {
  const page = pagination?.page || 1;
  const pageSize = pagination?.pageSize || 100;
  
  return useQuery({
    queryKey: ["expenses", filters, pagination],
    queryFn: async (): Promise<PaginatedResult<Expense>> => {
      // Use RPC when organizationIds filter is present OR when searchTerm is provided
      if ((filters?.organizationIds && filters.organizationIds.length > 0) || filters?.searchTerm) {
        const { data, error } = await supabase.rpc('search_property_expenses', {
          p_property_ids: filters.propertyIds || null,
          p_category_id: filters.categoryId || null,
          p_tag_id: filters.tagId || null,
          p_date_from: filters.dateFrom || null,
          p_date_to: filters.dateTo || null,
          p_status: filters.status || null,
          p_page: page,
          p_page_size: pageSize,
          p_order_by: pagination?.orderBy || 'expense_date',
          p_order_direction: pagination?.orderDirection || 'desc',
          p_organization_ids: filters.organizationIds || null,
          p_search_term: filters.searchTerm || null,
        });

        if (error) throw error;

        const results = data || [];
        const totalCount = results.length > 0 ? results[0].total_count : 0;

        return {
          data: results.map(row => ({
            id: row.id,
            expense_date: row.expense_date,
            property_id: row.property_id,
            category_id: row.category_id,
            subcategory_id: row.subcategory_id,
            description: row.description,
            amount: row.amount,
            payment_method: row.payment_method,
            receipt_url: row.receipt_url,
            tag_id: row.tag_id,
            status: row.status,
            created_at: row.created_at,
            updated_at: row.updated_at,
            properties: {
              short_name: row.property_short_name,
              title: row.property_title,
            },
            category: row.category_name ? { name: row.category_name } : undefined,
            subcategory: row.subcategory_name ? { name: row.subcategory_name } : undefined,
            expense_tags: row.tag_name ? {
              id: row.tag_id,
              name: row.tag_name,
              color: row.tag_color,
            } : undefined,
          })) as Expense[],
          totalCount: Number(totalCount),
          totalPages: Math.ceil(Number(totalCount) / pageSize),
          currentPage: page,
        };
      }

      // Fallback to PostgREST query when no organizationIds (retrocompatibility)
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from("properties_expenses")
        .select(
          `
          *,
          properties!inner(short_name, title),
          category:properties_expenses_categories!category_id(name),
          subcategory:properties_expenses_subcategories!subcategory_id(name),
          expense_tags:tag_id(id, name, color)
        `,
          { count: 'exact' }
        )
        .range(from, to)
        .order(pagination?.orderBy || "expense_date", { 
          ascending: pagination?.orderDirection === 'asc' 
        })
        .order("created_at", { ascending: false });

      if (filters?.propertyIds && filters.propertyIds.length > 0) {
        query = query.in("property_id", filters.propertyIds);
      }

      if (filters?.categoryId) {
        query = query.eq("category_id", filters.categoryId);
      }

      if (filters?.dateFrom) {
        query = query.gte("expense_date", filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte("expense_date", filters.dateTo);
      }

      if (filters?.status) {
        query = query.eq("status", filters.status);
      }

      if (filters?.tagId) {
        query = query.eq("tag_id", filters.tagId);
      }

      if (filters?.searchTerm) {
        const searchTerm = filters.searchTerm;
        query = query.or(`description.ilike.%${searchTerm}%,amount::text.ilike.%${searchTerm}%,category.name.ilike.%${searchTerm}%,subcategory.name.ilike.%${searchTerm}%`);
      }

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      return {
        data: (data || []) as Expense[],
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page,
      };
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
};
